import { Injectable, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, tap } from 'rxjs';
import { NExpeConfigService } from './nexpe.service';
import { NExpeConfig, NExpeRowData } from './nexpe.model';
import { ToasterService } from '../../layout/toaster/toaster.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class NExpeStore {
  // Injected service
  private readonly nexpeService = inject(NExpeConfigService);
  private readonly toasterService = inject(ToasterService);
  private readonly translate = inject(TranslateService);

  // Triggers for save operations
  private readonly saveOneTrigger = signal<{ config: NExpeConfig; timestamp: number } | null>(null);
  private readonly deleteTrigger = signal<{ nexpe: string; timestamp: number } | null>(null);

  // UI state
  private readonly searchQuery = signal<string>('');
  private readonly selectedNExpe = signal<NExpeConfig | null>(null);
  private readonly isModalOpen = signal<boolean>(false);

  // Publicly exposed signals
  readonly allConfigs = computed<NExpeConfig[]>(() => {
    return this.nexpeResource.value() as NExpeConfig[];
  });

  readonly isLoading = computed(
    () =>
      this.nexpeResource.isLoading() ||
      this.saveOneResource.isLoading() ||
      this.deleteResource.isLoading()
  );
  readonly error = computed(() => this.nexpeResource.error());

  // Resource to load configs from the API
  private readonly nexpeResource = rxResource({
    stream: () => this.nexpeService.getAllNExpeConfigs(),
  });

  // Resource to save one config
  readonly saveOneResource = rxResource({
    params: () => this.saveOneTrigger(),
    stream: ({ params }) => {
      if (!params) return EMPTY;
      return this.nexpeService.updateNExpeConfig([params.config]).pipe(
        tap(() => {
          queueMicrotask(() => {
            this.toasterService.success(this.translate.instant('GLOBAL.SAVE_SUCCESS'));
            this.nexpeResource.reload();
          });
        }),
        catchError((error) => {
          queueMicrotask(() => {
            this.toasterService.error(this.translate.instant('GLOBAL.SAVE_ERROR'));
          });
          throw error;
        })
      );
    },
  });

  // Resource to delete a NExpe configuration
  readonly deleteResource = rxResource({
    params: () => this.deleteTrigger(),
    stream: ({ params }) => {
      if (!params) return EMPTY;
      return this.nexpeService.deleteNExpeConfig(params.nexpe).pipe(
        tap(() => {
          queueMicrotask(() => {
            this.toasterService.success(this.translate.instant('NEXPE.DELETE_SUCCESS'));
            this.nexpeResource.reload();
          });
        }),
        catchError((error) => {
          queueMicrotask(() => {
            this.toasterService.error(this.translate.instant('NEXPE.DELETE_ERROR'));
          });
          throw error;
        })
      );
    },
  });

  // Transform configs into table data
  readonly nexpeRowData = computed<NExpeRowData[]>(() => {
    const configs = this.allConfigs();
    const data = configs.map((config) => ({
      name: config.name,
      nexpe: config.nexpe,
      allowedNExpeDeclaration: config.allowedNExpeDeclaration,
      allowedNexpeDegradation: config.allowedNexpeDegradation,
      allowedNexpeTroubleshooting: config.allowedNexpeTroubleshooting,
      allowedNexpeQuality: config.allowedNexpeQuality,
      allowedNexpeRecurrentDelay: config.allowedNexpeRecurrentDelay,
      allowedNexpeAddOrder: config.allowedNexpeAddOrder,
      allowedNexpeMissingSupport: config.allowedNexpeMissingSupport,
      allowedNexpeDelay: config.allowedNexpeDelay,
      allowedNExpeEvaluation: config.allowedNExpeEvaluation,
      usersForcedEvaluation: config.usersForcedEvaluation,
      hasContact: !!(config.customerService || config.food4rest),
      cutOffConfigured:
        !!config.cutOff &&
        Array.isArray(config.cutOff.timeSlots) &&
        config.cutOff.timeSlots.length > 0,
    }));

    // Sort: 'default' first, then others alphabetically
    return data.sort((a, b) => {
      if (a.nexpe.toLowerCase() === 'default') return -1;
      if (b.nexpe.toLowerCase() === 'default') return 1;
      return a.nexpe.localeCompare(b.nexpe);
    });
  });

  // Filter based on search
  readonly filteredNexpeData = computed<NExpeRowData[]>(() => {
    const data = this.nexpeRowData();
    const query = this.searchQuery().toLowerCase().trim();

    if (!query) return data;

    return data.filter(
      (row) =>
        row.nexpe.toLowerCase().includes(query) ||
        (row.name?.toLowerCase().includes(query) ?? false)
    );
  });

  // Modal state
  readonly currentNExpe = computed(() => this.selectedNExpe());
  readonly modalOpen = computed(() => this.isModalOpen());

  deleteNExpe(nexpe: string): void {
    this.deleteTrigger.set({ nexpe, timestamp: Date.now() });
  }

  setSearchQuery(query: string): void {
    this.searchQuery.set(query);
  }

  openNExpeDetail(nexpeId: string): void {
    const config = this.allConfigs().find((c) => c.nexpe === nexpeId);
    if (config) {
      this.selectedNExpe.set(config);
      this.isModalOpen.set(true);
    }
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedNExpe.set(null);
  }

  updateNExpeConfig(config: NExpeConfig): void {
    this.selectedNExpe.set(config);
  }

  saveNExpeConfig(): void {
    const config = this.selectedNExpe();
    if (!config) return;

    this.saveOneTrigger.set({ config, timestamp: Date.now() });
    this.closeModal();
  }

  openNewNExpeModal(): void {
    const newConfig: NExpeConfig = {
      nexpe: '',
      allowedNExpeDeclaration: false,
      allowedNexpeDegradation: false,
      allowedNexpeTroubleshooting: false,
      allowedNexpeQuality: false,
      allowedNexpeRecurrentDelay: false,
      allowedNexpeAddOrder: false,
      allowedNexpeMissingSupport: false,
      allowedNexpeDelay: false,
      allowedNExpeEvaluation: false,
      usersForcedEvaluation: false,
      customerService: '',
      food4rest: '',
      cutOff: null,
    };
    this.selectedNExpe.set(newConfig);
    this.isModalOpen.set(true);
  }
}
